import { useEffect, useRef, useState } from "react";
import { rpc } from "../../lib/rpc";
import { uuid4 } from "../../hooks/useFlowOperationId";
import { compareNaturalText } from "../../lib/naturalSort";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

export function AddArticleStockDialog({ open, onClose, article, onSaved }) {
    const [places, setPlaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [reload, setReload] = useState(0);
    const [search, setSearch] = useState("");
    const [compartmentId, setCompartmentId] = useState("");
    const [quantity, setQuantity] = useState("1");
    const [serialText, setSerialText] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [locked, setLocked] = useState(false);
    const pending = useRef(null);
    const inFlight = useRef(false);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        setLoadError("");
        Promise.all(["locations", "rooms", "shelves", "compartments"].map(kind => rpc.get(`/master/${kind}`)))
            .then(results => {
                if (!active) return;
                setLoading(false);
                if (results.some(result => !result.ok)) {
                    setLoadError("Lagerorte konnten nicht geladen werden. Bitte erneut versuchen.");
                    return;
                }
                const [locations, rooms, shelves, compartments] = results.map(result => result.data);
                const byId = rows => Object.fromEntries(rows.map(row => [row.id, row]));
                const locationMap = byId(locations), roomMap = byId(rooms), shelfMap = byId(shelves);
                setPlaces(compartments.map(compartment => {
                    const shelf = shelfMap[compartment.shelf_id];
                    const room = roomMap[shelf?.room_id];
                    const location = locationMap[room?.location_id];
                    return { id: compartment.id, label: [location?.name, room?.name, shelf?.name,
                        `${compartment.name} (${compartment.code})`].filter(Boolean).join(" / ") };
                }).sort((a, b) => compareNaturalText(a.label, b.label)));
            });
        return () => { active = false; };
    }, [open, reload]);

    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const filtered = places.filter(place => words.every(word => place.label.toLowerCase().includes(word)));
    const selected = places.find(place => place.id === compartmentId);
    const submit = async event => {
        event.preventDefault();
        if (inFlight.current) return;
        if (!pending.current) {
            const amount = Number(quantity);
            const serials = serialText.split(/\n/).map(value => value.trim()).filter(Boolean);
            if (!compartmentId || !Number.isSafeInteger(amount) || amount <= 0) {
                setError("Bitte einen Lagerort und eine ganze Menge größer als 0 eingeben.");
                return;
            }
            if (article.type === "single" && (serials.length !== amount || new Set(serials).size !== amount)) {
                setError("Bitte je Gerät eine eindeutige Seriennummer eingeben, eine pro Zeile.");
                return;
            }
            pending.current = { client_operation_id: uuid4(), positions: [{
                article_id: article.id, compartment_id: compartmentId, quantity: amount,
                serial_numbers: article.type === "single" ? serials : [],
            }] };
        }
        inFlight.current = true;
        setBusy(true);
        setLocked(true);
        setError("");
        try {
            const result = await rpc.post("/receipts/with-positions", pending.current);
            if (!result.ok) {
                // Validation failures have no stock effect for this single-position receipt.
                const rejected = [400, 404, 422].includes(result.status) ||
                    (result.status === 409 && /Seriennummer.*bereits erfasst|Artikel ist archiviert/.test(result.error));
                if (rejected) {
                    pending.current = null;
                    setLocked(false);
                    setError(`${result.error} — Bitte die Eingaben korrigieren.`);
                } else {
                    // An uncertain response must replay the exact same operation.
                    setError(`${result.error} — Bitte dieselbe Buchung erneut versuchen.`);
                }
                return;
            }
            pending.current = null;
            setLocked(false);
            setCompartmentId("");
            setQuantity("1");
            setSerialText("");
            setSearch("");
            onSaved();
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    return <Dialog open={open} onOpenChange={value => { if (!value && !busy) onClose(); }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="article-stock-dialog">
            <DialogHeader className="text-left">
                <DialogTitle>Lagerort hinzufügen</DialogTitle>
                <DialogDescription className="text-foreground/70">
                    Bestand für „{article.name}“ einlagern. Die Menge wird zum vorhandenen Bestand addiert und im Verlauf gebucht.
                </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="flex min-w-0 flex-col gap-4">
                {loading ? <p role="status">Lagerorte werden geladen …</p> : null}
                {loadError ? <div role="alert">{loadError} <Button type="button" variant="outline" onClick={() => setReload(value => value + 1)}>Erneut laden</Button></div> : null}
                <Label htmlFor="stock-place-search">Lagerort suchen</Label>
                <Input className="min-h-11 border-foreground/60 focus-visible:ring-foreground" id="stock-place-search" type="search" placeholder="Standort, Raum, Regal oder Fach" value={search} onChange={event => setSearch(event.target.value)} disabled={locked || loading || !!loadError} />
                <fieldset disabled={locked || loading || !!loadError} className="min-w-0">
                    <legend className="mb-2 text-sm">Lagerfach auswählen</legend>
                    <div className="max-h-48 overflow-auto">
                        {filtered.map(place => <label key={place.id} className="flex min-h-11 items-center gap-3 rounded p-2 text-sm focus-within:ring-2 focus-within:ring-foreground">
                            <input type="radio" name="stock-place" value={place.id} checked={compartmentId === place.id} onChange={() => setCompartmentId(place.id)} />
                            <span className="min-w-0 break-words">{place.label}</span>
                        </label>)}
                    </div>
                    {!loading && !loadError && !filtered.length ? <p role="status">Keine passenden Lagerfächer. Bitte den Suchbegriff ändern oder ein Fach in der Lagerstruktur anlegen.</p> : null}
                </fieldset>
                {selected ? <p className="break-words text-sm" role="status">Ausgewählt: {selected.label}</p> : null}
                <Label htmlFor="stock-add-quantity">Menge hinzufügen</Label>
                <Input className="min-h-11 border-foreground/60 focus-visible:ring-foreground" id="stock-add-quantity" type="number" min="1" step="1" required value={quantity} disabled={locked} onChange={event => setQuantity(event.target.value)} />
                {article.type === "single" ? <>
                    <Label htmlFor="stock-add-serials">Seriennummern (eine pro Gerät und Zeile)</Label>
                    <Textarea className="border-foreground/60 focus-visible:ring-foreground" id="stock-add-serials" required value={serialText} disabled={locked} onChange={event => setSerialText(event.target.value)} />
                </> : null}
                {error ? <p role="alert" className="text-sm">{error}</p> : null}
                <Button type="submit" variant="outline" className="min-h-11 focus-visible:ring-foreground" disabled={busy || loading || !!loadError || !compartmentId}>
                    {busy ? "Wird gebucht …" : locked ? "Buchung erneut versuchen" : "Bestand hinzufügen"}
                </Button>
            </form>
        </DialogContent>
    </Dialog>;
}
